import React, { type ReactNode, useState, useEffect } from 'react';
import Header from './Header';
import Footer from './Footer';
import SEOHead from './SEOHead';
import Breadcrumb from './Breadcrumb';
import ContactDialog from './ContactDialog';
import CookieDialog from './CookieDialog';
import ChatwootWidget from './ChatwootWidget';
import { useCookieManager } from '../hooks/useCookieManager';
import { useContactButtonHandler } from '../hooks/useContactButtonHandler';
import useSmoothScroll from '../hooks/useSmoothScroll';
import useWebVitals from '../hooks/useWebVitals';
import { preloadCriticalResources, addResourceHints } from '../utils/performance';

interface LayoutProps {
  children: ReactNode;
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  type?: string;
  onContactClick?: () => void;
  showBreadcrumbs?: boolean;
  includeReviewSchema?: boolean;
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tags?: string[];
  };
}

const Layout: React.FC<LayoutProps> = ({ 
  children, 
  title,
  description,
  keywords,
  image,
  type,
  showBreadcrumbs = false,
  includeReviewSchema = false,
  article
}) => {
  const [showContactDialog, setShowContactDialog] = useState(false);
  const { showCookieDialog, closeCookieDialog } = useCookieManager();
  
  // Smooth scrolling aktivieren
  useSmoothScroll();
  
  // Web Vitals Monitoring aktivieren
  useWebVitals();
  
  // Performance-Optimierungen beim ersten Laden
  useEffect(() => {
    preloadCriticalResources();
    addResourceHints();
    
    // Test: Custom Event Listener
    const handleCustomContactEvent = () => {
      console.log('Custom contact event received');
      setShowContactDialog(true);
    };
    
    document.addEventListener('contactButtonClick', handleCustomContactEvent);
    
    return () => {
      document.removeEventListener('contactButtonClick', handleCustomContactEvent);
    };
  }, []);

  const handleContactClick = () => {
    console.log('ContactDialog öffnen - handleContactClick called');
    setShowContactDialog(true);
  };

  const handleContactClose = () => {
    console.log('ContactDialog schließen - handleContactClose called');
    setShowContactDialog(false);
  };

  // Contact-Button Event-Handler für data-action="contact" Elemente
  useContactButtonHandler(handleContactClick);

  return (
    <>
      <SEOHead 
        title={title}
        description={description}
        keywords={keywords}
        image={image}
        type={type}
        article={article}
        includeReviewSchema={includeReviewSchema}
      />
      
      <div className="app-container">
        <Header onContactClick={handleContactClick} />
        
        <main className="main-content">
          {showBreadcrumbs && <Breadcrumb />}
          {children}
        </main>
        
        <Footer onContactClick={handleContactClick} />

        {/* Dialoge */}
        <ContactDialog 
          isVisible={showContactDialog}
          onClose={handleContactClose}
        />

        <CookieDialog 
          isVisible={showCookieDialog}
          onClose={closeCookieDialog}
        />

        {/* Chatwoot Widget */}
        <ChatwootWidget />
      </div>
    </>
  );
};

export default Layout;
